import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { toast } from 'sonner';
import {
  Users,
  UserPlus,
  Clock,
  Calendar,
  Wallet,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Building2,
  RefreshCw,
  ArrowRight,
  Briefcase } from
'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from
'recharts';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const StatCard = ({ title, value, subtitle, icon: Icon, color = 'green' }) => {const { t } = useTranslation("hr");
  const colorClasses = {
    green: 'bg-green-500/20 text-green-500',
    blue: 'bg-blue-500/20 text-blue-500',
    amber: 'bg-amber-500/20 text-amber-500',
    purple: 'bg-purple-500/20 text-purple-500',
    red: 'bg-red-500/20 text-red-500'
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>);

};

const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];

export const HRDashboard = () => {const { t } = useTranslation("hr");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/hr/dashboard`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch dashboard');
      const json = await res.json();
      setData(json);
    } catch (err) {
      toast.error(err.message);
      setData({
        headcount: { active: 0, inactive: 0, on_leave: 0 },
        new_hires_this_month: 0,
        pending_time_entries: 0,
        pending_leave_requests: 0,
        payroll_status: { current_period: null },
        department_breakdown: [],
        upcoming_leaves: [],
        recent_activity: []
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {fetchDashboard();}, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500" />
      </div>);

  }

  const headcount = data?.headcount || {};
  const totalEmployees = (headcount.active || 0) + (headcount.inactive || 0) + (headcount.on_leave || 0);
  const deptData = data?.department_breakdown || [];
  const upcomingLeaves = data?.upcoming_leaves || [];

  const quickActions = [
  { name: 'Add Employee', href: '/hr/employees', icon: UserPlus },
  { name: 'Time Entries', href: '/hr/time-attendance', icon: Clock },
  { name: 'Leave Requests', href: '/hr/leave', icon: Calendar },
  { name: 'Run Payroll', href: '/hr/payroll', icon: Wallet }];


  return (
    <div className="space-y-6" data-testid="hr-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("hrDashboard", "HR Dashboard")}</h1>
          <p className="text-muted-foreground mt-1">{t("humanResourcesOverview", "Human Resources overview and management")}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchDashboard} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}
          </Button>
          <Link to="/hr/employees">
            <Button className="gap-2 bg-green-600 hover:bg-green-700">
              <Users className="h-4 w-4" />{t("viewEmployees", "View Employees")}
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Total Employees" value={totalEmployees} subtitle={`${headcount.active || 0} active`} icon={Users} color="green" />
        <StatCard title="New Hires" value={data?.new_hires_this_month || 0} subtitle="This month" icon={UserPlus} color="blue" />
        <StatCard title="Pending Time" value={data?.pending_time_entries || 0} subtitle="Awaiting approval" icon={Clock} color="amber" />
        <StatCard title="Leave Requests" value={data?.pending_leave_requests || 0} subtitle="Pending review" icon={Calendar} color="purple" />
      </div>

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-4">
        {quickActions.map((action) =>
        <Link key={action.name} to={action.href}>
            <Card className="hover:border-green-500 transition-colors cursor-pointer h-full">
              <CardContent className="pt-6 flex flex-col items-center text-center">
                <action.icon className="h-8 w-8 text-green-500 mb-3" />
                <h3 className="font-semibold">{action.name}</h3>
              </CardContent>
            </Card>
          </Link>
        )}
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Department Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Building2 className="h-5 w-5 text-green-500" />{t("headcountByDepartment", "Headcount by Department")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {deptData.length === 0 ?
            <p className="text-center text-muted-foreground py-8">{t("noDepartmentData", "No department data available")}</p> :

            <ResponsiveContainer width="100%" height={250}>
                <BarChart data={deptData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis type="number" stroke="#9ca3af" />
                  <YAxis dataKey="department_name" type="category" stroke="#9ca3af" width={100} />
                  <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }} />
                  <Bar dataKey="count" fill="#22c55e" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            }
          </CardContent>
        </Card>

        {/* Employee Status Pie */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-blue-500" />{t("employeeStatus", "Employee Status")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {totalEmployees === 0 ?
            <p className="text-center text-muted-foreground py-8">{t("noEmployeesYet", "No employees yet")}</p> :

            <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                  data={[
                  { name: 'Active', value: headcount.active || 0 },
                  { name: 'On Leave', value: headcount.on_leave || 0 },
                  { name: 'Inactive', value: headcount.inactive || 0 }]
                  }
                  cx="50%" cy="50%" outerRadius={80}
                  dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                  
                    {[0, 1, 2].map((_, index) =>
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  )}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }} />
                </PieChart>
              </ResponsiveContainer>
            }
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Upcoming Leaves */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Calendar className="h-5 w-5 text-amber-500" />{t("upcomingLeaves", "Upcoming Leaves")}
            </CardTitle>
            <Link to="/hr/leave">
              <Button variant="ghost" size="sm" className="gap-2">{t("viewAll", "View All")}<ArrowRight className="h-4 w-4" /></Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {upcomingLeaves.length === 0 ?
              <div className="text-center py-8">
                  <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-2" />
                  <p className="text-muted-foreground">{t("noUpcomingLeaves", "No upcoming leaves")}</p>
                </div> :

              upcomingLeaves.slice(0, 5).map((leave) =>
              <div key={leave.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                    <div>
                      <p className="font-medium">{leave.employee_name}</p>
                      <p className="text-sm text-muted-foreground">{leave.leave_type}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm">{leave.start_date}</p>
                      <Badge variant="secondary">{leave.total_days}{t("days", "days")}</Badge>
                    </div>
                  </div>
              )
              }
            </div>
          </CardContent>
        </Card>

        {/* Payroll Status */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Wallet className="h-5 w-5 text-purple-500" />{t("payrollStatus", "Payroll Status")}
            </CardTitle>
            <Link to="/hr/payroll">
              <Button variant="ghost" size="sm" className="gap-2">{t("viewPayroll", "View Payroll")}<ArrowRight className="h-4 w-4" /></Button>
            </Link>
          </CardHeader>
          <CardContent>
            {data?.payroll_status?.current_period ?
            <div className="space-y-4">
                <div className="p-4 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground">{t("currentPeriod", "Current Period")}</p>
                  <p className="text-xl font-bold">{data.payroll_status.current_period}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                    <p className="text-sm text-muted-foreground">{t("status", "Status")}</p>
                    <Badge className="mt-1 bg-green-600">{data.payroll_status.status || 'Pending'}</Badge>
                  </div>
                  <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                    <p className="text-sm text-muted-foreground">{t("employees", "Employees")}</p>
                    <p className="font-bold">{data.payroll_status.employee_count || 0}</p>
                  </div>
                </div>
              </div> :

            <div className="text-center py-8">
                <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-2" />
                <p className="text-muted-foreground">{t("noPayrollRun", "No payroll run found")}</p>
                <Link to="/hr/payroll">
                  <Button className="mt-4 bg-green-600 hover:bg-green-700">{t("createPayrollRun", "Create Payroll Run")}</Button>
                </Link>
              </div>
            }
          </CardContent>
        </Card>
      </div>
    </div>);

};

export default HRDashboard;